// The interface's building blocks, all made with el(): one button family, a card, a list row, a
// pill, a progress bar, a sheet (dialog) and a confirm. Every screen is built from these so the
// game looks like one thing.
import { el } from './dom.js';
import { icon } from './icons.js';

// button('Buy', { kind: 'primary', size: 'md', icon: 'coin', onclick, disabled, extra: 'act' })
export function button(label, o = {}) {
  const b = el(`button.btn.btn-${o.kind || 'secondary'}.btn-${o.size || 'md'}`, {
    type: 'button',
    onclick: o.onclick,
    disabled: !!o.disabled,
    title: o.title,
    'aria-label': o.ariaLabel,
    class: o.extra,
  });
  if (o.icon) b.append(icon(o.icon, o.size === 'lg' ? 22 : 18));
  if (label !== undefined && label !== null && label !== '') b.append(el('span.label', null, label));
  return b;
}

export function card(children, o = {}) {
  return el(`div.card${o.padded === false ? '.flush' : ''}`, { class: o.extra }, children);
}

// A list row: icon | title + meta | value or action or chevron. Clickable when onclick is given.
export function row(o = {}) {
  const clickable = typeof o.onclick === 'function' && !o.disabled;
  const node = el(clickable ? 'button.row' : 'div.row', { type: clickable ? 'button' : undefined, onclick: clickable ? o.onclick : undefined, 'aria-disabled': o.disabled ? 'true' : undefined, class: `${o.disabled ? 'is-disabled' : ''} ${o.extra || ''}`.trim() || undefined });
  if (o.icon) node.append(el('span.row-ic', { class: o.tone ? `tone-${o.tone}` : undefined }, icon(o.icon, 20)));
  const main = el('div.row-main', null, el('div.row-title', null, o.title));
  if (o.meta) main.append(el('div.row-meta', null, o.meta));
  if (o.disabled && o.reason) main.append(el('div.row-meta.muted', null, o.reason));
  node.append(main);
  const end = el('div.row-end');
  if (o.value !== undefined && o.value !== null && o.value !== '') end.append(el('span.row-value', { class: o.valueTone ? `tone-${o.valueTone}` : undefined }, o.value));
  if (o.action) end.append(o.action);
  if (o.chevron || (clickable && !o.action && o.chevron !== false)) end.append(el('span.chevron', null, icon('chevron', 18)));
  if (end.childNodes.length) node.append(end);
  return node;
}

export function pill(text, kind = 'neutral') {
  return el(`span.pill.pill-${kind}`, null, text);
}

// A thin progress bar. node.set(v) writes the width only when the rounded value changes.
export function progress(value, o = {}) {
  const fill = el('i');
  const node = el(`div.prog${o.kind ? `.prog-${o.kind}` : ''}`, { role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-label': o.label }, fill);
  let last = -1;
  node.set = (v) => {
    const r = Math.max(0, Math.min(100, Math.round(v)));
    if (r === last) return;
    last = r;
    fill.style.width = r + '%';
    node.setAttribute('aria-valuenow', String(r));
    node.classList.toggle('is-low', r < 25);
    node.classList.toggle('is-mid', r >= 25 && r < 50);
  };
  node.set(value);
  return node;
}

export function empty(text, iconName = 'info') {
  return el('div.empty', null, icon(iconName, 28), el('p', null, text));
}

export function section(title, extra) {
  return el('h3.section', null, title, extra);
}

// Keeps Tab inside a dialog while it is open. Returns a function that removes the trap.
export function trapFocus(node) {
  const onKey = (e) => {
    if (e.key !== 'Tab') return;
    const items = [...node.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((n) => !n.disabled && n.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  };
  node.addEventListener('keydown', onKey);
  return () => node.removeEventListener('keydown', onKey);
}

// A bottom sheet or side dialog with a handle, a head (back or close, title) and a body.
export function sheet(o = {}) {
  const body = el('div.sheet-body', null, o.children);
  const head = el(
    'div.sheet-head',
    null,
    o.onBack ? button('', { kind: 'ghost', size: 'sm', icon: 'back', onclick: o.onBack, ariaLabel: 'Back' }) : null,
    el('h2', { id: o.id ? `${o.id}-title` : undefined, tabindex: '-1' }, o.title),
    o.sub ? el('span.sheet-sub', null, o.sub) : null,
    button('', { kind: 'ghost', size: 'sm', icon: 'close', onclick: o.onClose, ariaLabel: 'Close', extra: 'close' })
  );
  const node = el('div.sheet', { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': o.id ? `${o.id}-title` : undefined, id: o.id }, el('div.sheet-handle', { 'aria-hidden': 'true' }), head, body);
  const untrap = trapFocus(node);
  return { node, head, body, close: () => { untrap(); node.remove(); } };
}

// confirm({ title, text, cost, okLabel, danger }) inside a parent: resolves true or false.
export function confirm(parent, o = {}) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v) => {
      if (done) return;
      done = true;
      overlay.remove();
      document.removeEventListener('keydown', onKey);
      resolve(v);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        finish(false);
      }
    };
    const ok = button(o.okLabel || 'Yes', { kind: o.danger ? 'danger' : 'primary', onclick: () => finish(true) });
    const box = el(
      'div.confirm',
      { role: 'alertdialog', 'aria-modal': 'true' },
      el('h3', null, o.title),
      o.text ? el('p', null, o.text) : null,
      o.cost ? el('p.confirm-cost', null, icon('coin', 18), ' ', o.cost) : null,
      el('div.confirm-actions', null, button(o.cancelLabel || 'Not now', { kind: 'ghost', onclick: () => finish(false) }), ok)
    );
    const overlay = el('div.confirm-overlay', { onclick: (e) => { if (e.target === overlay) finish(false); } }, box);
    parent.append(overlay);
    document.addEventListener('keydown', onKey);
    ok.focus();
  });
}

export function kbd(text) {
  return el('kbd', null, text);
}
